import { VisitPopulated } from '@api/READ_ONLY/visits_api/Api';
import {
  DEFAULT_LIST_INITIAL_STATE,
  DEFAULT_LIST_STORAGE,
  LIST_TABLE_APPEARANCE,
} from '@app/common/listTableAppearance';
import { Filter, JoinOperator } from '@app/components/query-filters/filters.types';
import { ADVANCED_FILTER_MODAL_SETTINGS } from '@app/config/advancedFilterModal';
import { APP_PREFIX, DOCTOR_PORTAL_PAGE, DOCTOR_PORTAL_VISITS } from '@app/routes/urls';
import { PRESERVED_QUERY_PARAMS } from '@app/utils/queryParams';
import VisitsQuickFilter from '@feat-visits/components/VisitsQuickFilter/VisitsQuickFilter';
import { getVisitTableColumns, VisitRow } from '@feat-visits/components/VisitTable/visitTableColumns.config';
import { getVisitFilterColumns } from '@feat-visits/config/visitsFilters.config';
import { deserializeFiltersFromUrl, generateQueryParams } from '@konnecta/generic-table';
import { AppDataTable } from '@app/components/common/AppDataTable';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { listDoctorVisits } from '@app/features/doctorPortal/api';
import { doctorPatientCardPath } from '@app/features/doctorPortal/doctorLinks';
import DoctorVisitModal from '@app/features/doctorPortal/DoctorVisitModal';

/**
 * The doctor's visits.
 *
 * This IS the visits list — same GenericDataTable, same
 * `getVisitTableColumns`, same advanced-filter columns, same `listVisits`
 * handler behind `/doctor/visits`. Not a doctor-flavoured copy: a second list
 * would drift from this one's columns and filters the first time either was
 * touched, and the doctor would slowly be looking at a different product.
 *
 * What differs is only what a doctor may do with it:
 *
 *   ROWS      narrowed by visits_select_policy to their own patients' visits.
 *             There is no client-side filter doing this and no doctor id in the
 *             request — widening it is not expressible here. The `/doctor`
 *             prefix is what makes the request run as a doctor, so an account
 *             that is also a caregiver still gets the doctor's rows here.
 *   FINANCE   both money flags off. Not cost and profit alone: what the patient
 *             is charged, the discount and the final price are no more a
 *             doctor's business than the margin is, and the backend already
 *             strips every one of them for `doc` — so left on, these columns
 *             rendered four dashes and an advanced filter that answered nothing.
 *             Both flags are required rather than defaulted precisely so the
 *             decision is made and not inherited.
 *   ACTIONS   none of the administrative ones — no bulk delete, no assign, no
 *             export, no status changes. A doctor cannot move or cancel a visit,
 *             and the backend refuses it whatever this page offers.
 *
 * Opening a row gives the doctor's own surface: read the visit, comment on it,
 * propose a task.
 */

const DOCTOR_VISIT_URL_PARAM_CONFIG = {
  external: {
    // GenericDataTable's search only reaches the fetch when the external param
    // is named; without this the box types into nothing.
    searchTerm: 'search',
  },
} as const;

const DOCTOR_VISITS_PATH = `/${APP_PREFIX}/${DOCTOR_PORTAL_PAGE}/${DOCTOR_PORTAL_VISITS}`;

// Who staffed a visit is the office's business, not the doctor's — they read the
// clinical record, not the roster. Dropped here rather than behind a flag on the
// shared builder: the two money flags are required BECAUSE forgetting one leaks
// what a role must never see, and this is a narrower thing, a display choice for
// one list.
//
// All FOUR caregiver filters go, not just the picker: leaving the free-text name
// filters would let the doctor search by a caregiver's name, which is the same
// question asked a different way.
const CAREGIVER_COLUMN_IDS = new Set(['caregivers']);
const CAREGIVER_FILTER_NAMES = new Set([
  'caregiver_id',
  'caregiver_first_name',
  'caregiver_last_name',
  'has_assigned_caregivers',
]);

const DoctorVisitsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [openVisitId, setOpenVisitId] = useState<number | null>(null);
  const [filterKey, setFilterKey] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);

  const transformVisitToRow = (visit: VisitPopulated): VisitRow => ({ ...visit, id: visit.id });

  const fetchVisits = useCallback(
    async (params: { limit: number; offset: number; query?: string; sort?: string; [key: string]: unknown }) => {
      // `aggregations` is dropped deliberately: the doctor's list has no
      // aggregate row, and forwarding the param makes the backend re-run the
      // whole filter for a total nothing renders.
      const { limit, offset, query, sort, aggregations, ...otherParams } = params;
      void aggregations;

      const filterParams: Record<string, string> = {};
      new URLSearchParams(window.location.search).forEach((value, key) => {
        if (key.startsWith('filter[')) filterParams[key] = value;
      });

      const response = await listDoctorVisits({
        limit,
        offset,
        ...(query && { query }),
        sort: sort || '-start_time',
        ...filterParams,
        ...otherParams,
      });

      return {
        data: response.data,
        total: response.total || response.data.length,
      };
    },
    []
  );

  // `false, false` for the same reason the table columns hide them: money is not
  // a doctor's business, and an advanced filter on it would be a way back to the
  // numbers the columns refuse to show.
  const visitFilterColumns = useMemo(
    () => getVisitFilterColumns(t, false, false).filter((column) => !CAREGIVER_FILTER_NAMES.has(column.name)),
    [t]
  );

  const initialFiltersFromUrl = useMemo(
    () => deserializeFiltersFromUrl(searchParams, visitFilterColumns),
    [searchParams, visitFilterColumns]
  );

  // The applied filters live in the URL — `fetchVisits` reads `filter[...]` from
  // it — and the table only re-reads it when it remounts. `navigate()` commits
  // asynchronously under the data router, so bumping `reloadKey` straight after
  // it would refetch against the pre-filter URL; this effect bumps it once the
  // params have actually landed. Same mechanism as the staff visits list.
  const filterSignature = useMemo(() => {
    const parts: string[] = [];
    searchParams.forEach((value, key) => {
      if (key.startsWith('filter[') || key === '_op') {
        parts.push(`${key}=${value}`);
      }
    });
    return parts.sort().join('&');
  }, [searchParams]);

  // A doctor-instruction notification links straight to one visit. The portal has
  // no route for a single visit - the modal opens over this list - so the link is
  // this page with `?visit=<id>`, read here. Not a `filter[` param, so it never
  // touches the fetch.
  useEffect(() => {
    const fromUrl = Number(searchParams.get('visit'));
    if (Number.isInteger(fromUrl) && fromUrl > 0) setOpenVisitId(fromUrl);
  }, [searchParams]);

  // Closing drops the param with it: left in the URL, a reload - or a second
  // click on the same notification - would reopen a modal the reader just closed.
  const closeVisitModal = useCallback(() => {
    setOpenVisitId(null);
    if (!searchParams.has('visit')) return;

    const next = new URLSearchParams(searchParams);
    next.delete('visit');
    const search = next.toString();
    navigate(`${DOCTOR_VISITS_PATH}${search ? `?${search}` : ''}`, { replace: true });
  }, [navigate, searchParams]);

  const isInitialFilterSync = useRef(true);
  useEffect(() => {
    if (isInitialFilterSync.current) {
      // Skip the mount run: the table already fetches from the initial URL.
      isInitialFilterSync.current = false;
      return;
    }
    setReloadKey((k) => k + 1);
  }, [filterSignature]);

  const handleApplyFilters = useCallback(
    (filters: Filter[], joinOperator: JoinOperator = '&&') => {
      const newParams = new URLSearchParams(generateQueryParams(filters, joinOperator));

      // Preserve only non-filter params (search, pageSize, etc.), but reset page to 1
      searchParams.forEach((value, key) => {
        if (!key.startsWith('filter[') && key !== '_op' && key !== 'page') {
          newParams.set(key, value);
        }
      });
      newParams.set('page', '1');

      const newSearch = newParams.toString();
      navigate(`${DOCTOR_VISITS_PATH}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
    },
    [navigate, searchParams]
  );

  const handleClearFilters = useCallback(() => {
    const newParams = new URLSearchParams();

    PRESERVED_QUERY_PARAMS.forEach((key) => {
      const value = searchParams.get(key);
      if (value) {
        newParams.set(key, value);
      }
    });

    const newSearch = newParams.toString();

    // Force reset the QueryFilters component by changing the key
    setFilterKey((prev) => prev + 1);

    navigate(`${DOCTOR_VISITS_PATH}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
  }, [navigate, searchParams]);

  const tableConfig = useMemo(
    () => ({
      // A table id of its own: column widths and visibility are a per-view
      // preference, and a doctor's are not an administrator's.
      tableId: 'doctor-visits',
      initialState: DEFAULT_LIST_INITIAL_STATE,
      storage: DEFAULT_LIST_STORAGE,
      urlParamConfig: DOCTOR_VISIT_URL_PARAM_CONFIG,
      fetchData: fetchVisits,
      transformData: transformVisitToRow,
      rowKey: 'id' as const,
      columns: getVisitTableColumns(t, {
        // The patient's name goes to the doctor's own patient dashboard, not the
        // staff client card the shared column defaults to — that route does not
        // resolve for a doctor. Both the handler and the href are given so a
        // cmd-click opens the same page in a new tab as a plain click does.
        onClientClick: (clientId) => navigate(doctorPatientCardPath(clientId)),
        clientCardHref: doctorPatientCardPath,
        // A doctor still has no caregiver record page to reach.
        onCaregiverClick: () => undefined,
        onVisitClick: (visitId) => setOpenVisitId(Number(visitId)),
        showInternalFinancials: false,
        showClientPricing: false,
      }).filter((column) => !CAREGIVER_COLUMN_IDS.has(column.id)),
      onRowDoubleClick: (row: VisitRow) => setOpenVisitId(Number(row.id)),
      searchPlaceholder: t('visits-list-search-placeholder'),
      advancedFilterConfig: {
        columns: visitFilterColumns,
        onApplyFilters: handleApplyFilters,
        onClearFilters: handleClearFilters,
        showDebugPanel: false,
        ...ADVANCED_FILTER_MODAL_SETTINGS,
        joinOperator: initialFiltersFromUrl.joinOperator,
        initialFilters: initialFiltersFromUrl.filters,
        key: filterKey,
      },
      actions: {
        getActions: (row: VisitRow) => [
          {
            key: 'open',
            label: t('doctor-visit-open-action'),
            onClick: () => setOpenVisitId(Number(row.id)),
          },
        ],
      },
    }),
    [
      fetchVisits,
      t,
      navigate,
      visitFilterColumns,
      handleApplyFilters,
      handleClearFilters,
      initialFiltersFromUrl,
      filterKey,
    ]
  );

  return (
    <>
      {/* min-h-0 so the table can scroll inside the flex column rather than
          pushing the page. */}
      <div className="flex-1 min-h-0">
        <AppDataTable
          appearance={LIST_TABLE_APPEARANCE}
          key={reloadKey}
          config={tableConfig}
          // The same quick filter the staff list has, on the same
          // `handleApplyFilters` the advanced modal uses — so a quick range and a
          // modal filter cannot disagree about what is applied.
          leftContent={<VisitsQuickFilter onApplyFilters={handleApplyFilters} />}
        />
      </div>
      <DoctorVisitModal visitId={openVisitId} onClose={closeVisitModal} />
    </>
  );
};

export default DoctorVisitsPage;
